import { useSubscriptionStore } from '../store/subscriptionStore';
import { FeatureType, SubscriptionTier } from '../types';

export const useFeatureAccess = () => {
  const {
    canAccessFeature,
    getCurrentTier,
    hasActiveSubscription,
    isInTrial,
    isInGracePeriod,
    isTrialExpired,
    getTrialDaysRemaining,
    getGracePeriodDaysRemaining,
    featureAccess,
  } = useSubscriptionStore();

  const currentTier = getCurrentTier();
  const activeSubscription = hasActiveSubscription();
  const inTrial = isInTrial();
  const inGracePeriod = isInGracePeriod();
  const trialExpired = isTrialExpired();
  const trialDaysLeft = getTrialDaysRemaining();
  const gracePeriodDaysLeft = getGracePeriodDaysRemaining();

  return {
    // Feature access
    canAccessBasicHabits: canAccessFeature(FeatureType.BasicHabits),
    canAccessAnalytics: canAccessFeature(FeatureType.Analytics),
    canAccessWeeklyInsights: canAccessFeature(FeatureType.WeeklyInsights),
    canExportData: canAccessFeature(FeatureType.Export),
    hasPrioritySupport: canAccessFeature(FeatureType.PrioritySupport),
    canCreateMoreHabits: canAccessFeature(FeatureType.HabitLimit),

    // Subscription status
    currentTier,
    hasActiveSubscription: activeSubscription,
    isInTrial: inTrial,
    isInGracePeriod: inGracePeriod,
    isTrialExpired: trialExpired,
    trialDaysRemaining: trialDaysLeft,
    gracePeriodDaysRemaining: gracePeriodDaysLeft,

    // Tier checks
    isFree: currentTier === SubscriptionTier.Free,
    isBasic: currentTier === SubscriptionTier.Basic,
    isPro: currentTier === SubscriptionTier.Pro,

    // Status helpers
    needsSubscription: !activeSubscription && !inTrial && !inGracePeriod,
    isExpiring: inTrial && trialDaysLeft !== null && trialDaysLeft <= 3,
    isInGrace: inGracePeriod && gracePeriodDaysLeft !== null && gracePeriodDaysLeft > 0,
    hasTrialExpired: trialExpired,

    // User-friendly status text
    getStatusText: () => {
      if (inTrial && trialDaysLeft !== null) {
        return `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in trial`;
      }
      
      if (inGracePeriod && gracePeriodDaysLeft !== null) {
        return `${gracePeriodDaysLeft} day${gracePeriodDaysLeft === 1 ? '' : 's'} left to subscribe`;
      }
      
      if (trialExpired) {
        return 'Your free trial has expired';
      }
      
      if (activeSubscription) {
        switch (currentTier) {
          case SubscriptionTier.Basic:
            return 'Basic Plan';
          case SubscriptionTier.Pro:
            return 'Pro Plan';
          default:
            return 'Active Subscription';
        }
      }
      
      return 'No active subscription';
    },

    // Get tier display name
    getTierDisplayName: (tier?: SubscriptionTier) => {
      const targetTier = tier ?? currentTier;
      switch (targetTier) {
        case SubscriptionTier.Free:
          return 'Free';
        case SubscriptionTier.Basic:
          return 'Basic';
        case SubscriptionTier.Pro:
          return 'Pro';
        default:
          return 'Unknown';
      }
    },

    // Check if user should see upgrade prompts
    shouldShowUpgradePrompt: (feature: FeatureType) => {
      if (canAccessFeature(feature)) {
        return false; // Already has access
      }

      // Don't show prompts during trial or grace period
      if (inTrial || inGracePeriod) {
        return false;
      }

      return true;
    },

    // Get upgrade CTA text
    getUpgradeCtaText: (feature: FeatureType) => {
      switch (feature) {
        case FeatureType.Analytics:
          return 'Upgrade to Pro for Analytics';
        case FeatureType.Export:
          return 'Upgrade to Basic for Data Export';
        case FeatureType.PrioritySupport:
          return 'Upgrade to Pro for Priority Support';
        case FeatureType.HabitLimit:
          return 'Upgrade for Unlimited Habits';
        default:
          return 'Upgrade Your Plan';
      }
    },

    // Habit limit information
    currentHabitCount: featureAccess?.currentHabitCount ?? 0,
    maxHabitsAllowed: featureAccess?.maxHabitsAllowed ?? 3,

    // AI usage information
    aiSuggestionsUsedToday: featureAccess?.aiSuggestionsUsedToday ?? 0,
    maxAiSuggestionsPerDay: featureAccess?.maxAiSuggestionsPerDay ?? 5,
    canUseAiToday: featureAccess?.canUseAiToday ?? true,

    // Raw feature access object
    featureAccess,
  };
}; 